import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard, Building2, CreditCard, Sparkles, Tag, Store,
  Activity, ScrollText, Settings as Cog, ChevronsLeft, ChevronsRight, Wand2, ShieldAlert,
  Compass, Lightbulb, Tags, Users,
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { BrandMark, BrandWordmark } from '@/components/brand/BrandLogo';

const NAV_GROUPS = [
  {
    label: 'Overview',
    items: [
      { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, end: true, testid: 'admin-nav-dashboard' },
    ],
  },
  {
    label: 'Catalog',
    items: [
      { to: '/admin/banks', label: 'Banks', icon: Building2, testid: 'admin-nav-banks' },
      { to: '/admin/cards', label: 'Cards', icon: CreditCard, testid: 'admin-nav-cards' },
      { to: '/admin/ingest', label: 'Ingest card', icon: Wand2, testid: 'admin-nav-ingest' },
      { to: '/admin/ingestion-jobs', label: 'Ingestion jobs', icon: Activity, testid: 'admin-nav-ingestion-jobs' },
    ],
  },
  {
    label: 'Vocabulary',
    items: [
      { to: '/admin/categories', label: 'Categories', icon: Tags, testid: 'admin-nav-categories' },
    ],
  },
  {
    label: 'Intelligence',
    items: [
      { to: '/admin/routing-hints', label: 'Routing hints', icon: Compass, testid: 'admin-nav-routing-hints' },
      { to: '/admin/routing-suggestions', label: 'Suggested hints', icon: Lightbulb, testid: 'admin-nav-routing-suggestions' },
      { to: '/admin/mccs', label: 'MCCs', icon: Tag, disabled: true, soon: true, testid: 'admin-nav-mccs' },
      { to: '/admin/offers', label: 'Offers', icon: Sparkles, disabled: true, soon: true, testid: 'admin-nav-offers' },
      { to: '/admin/merchants', label: 'Merchants', icon: Store, disabled: true, soon: true, testid: 'admin-nav-merchants' },
    ],
  },
  {
    label: 'Operations',
    items: [
      { to: '/admin/qa', label: 'Rule QA', icon: ShieldAlert, testid: 'admin-nav-qa' },
      { to: '/admin/users', label: 'Customers', icon: Users, testid: 'admin-nav-users' },
      { to: '/admin/audit-log', label: 'Audit Log', icon: ScrollText, testid: 'admin-nav-audit-log' },
    ],
  },
];

export const AdminSidebar = ({ collapsed, onToggle }) => {
  return (
    <TooltipProvider delayDuration={300}>
      <aside
        data-testid="admin-shell-sidebar"
        className={cn(
          'hidden lg:flex flex-col shrink-0 sticky top-0 h-screen border-r border-border bg-[hsl(var(--surface-2))] transition-[width] duration-200',
          collapsed ? 'w-14' : 'w-60'
        )}
      >
        <div className={cn('px-3 pt-4 pb-3 flex items-center', collapsed ? 'justify-center' : 'gap-2.5')}>
          <NavLink to="/admin" className="flex items-center min-w-0 flex-1" data-testid="admin-shell-logo-link">
            {collapsed ? (
              <BrandMark size={40} className="rounded-xl bg-[hsl(var(--background))] p-1 border border-border" />
            ) : (
              <div className="flex flex-col gap-1 items-start min-w-0">
                <BrandWordmark height={44} />
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground pl-[2px]">Operations</div>
              </div>
            )}
          </NavLink>
        </div>

        <nav className="flex-1 px-2 py-2 overflow-y-auto scrollbar-thin">
          {NAV_GROUPS.map((group) => (
            <div key={group.label} className="mb-3">
              {!collapsed && (
                <div className="px-2 mt-2 mb-1 text-[10px] uppercase tracking-[0.18em] text-muted-foreground/70">
                  {group.label}
                </div>
              )}
              <div className="space-y-0.5">
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const link = (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.end}
                      data-testid={item.testid}
                      onClick={(e) => { if (item.disabled) e.preventDefault(); }}
                      className={({ isActive }) => cn(
                        'flex items-center gap-3 px-2.5 py-2 rounded-lg text-sm transition-colors group relative',
                        item.disabled
                          ? 'opacity-50 cursor-not-allowed'
                          : 'hover:bg-[hsl(var(--background))]',
                        isActive && !item.disabled
                          ? 'bg-[hsl(var(--background))] text-foreground font-medium before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-[2px] before:bg-accent before:rounded-r'
                          : 'text-foreground/75 hover:text-foreground'
                      )}
                    >
                      <Icon className="h-[18px] w-[18px] shrink-0" />
                      {!collapsed && (
                        <>
                          <span className="flex-1 truncate">{item.label}</span>
                          {item.soon && <Badge variant="outline" className="text-[9px] uppercase tracking-wider h-4 px-1.5">Soon</Badge>}
                        </>
                      )}
                    </NavLink>
                  );
                  if (collapsed) {
                    return (
                      <Tooltip key={item.to}>
                        <TooltipTrigger asChild>{link}</TooltipTrigger>
                        <TooltipContent side="right">{item.label}{item.soon ? ' · soon' : ''}</TooltipContent>
                      </Tooltip>
                    );
                  }
                  return link;
                })}
              </div>
            </div>
          ))}
        </nav>

        <div className="border-t border-border p-2 flex items-center justify-between">
          <NavLink
            to="/dashboard"
            className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 px-2 py-1 rounded transition-colors"
            data-testid="admin-nav-exit-to-consumer"
          >
            {!collapsed && <span>← Exit to app</span>}
          </NavLink>
          <button
            onClick={onToggle}
            className="h-7 w-7 grid place-items-center rounded-md text-muted-foreground hover:bg-[hsl(var(--background))] hover:text-foreground"
            data-testid="admin-shell-sidebar-toggle"
            aria-label="Toggle sidebar"
          >
            {collapsed ? <ChevronsRight className="h-4 w-4" /> : <ChevronsLeft className="h-4 w-4" />}
          </button>
        </div>
      </aside>
    </TooltipProvider>
  );
};
